import { Vue, Component, Watch } from "vue-property-decorator";
import { namespace, Getter, Action } from "vuex-class";
import {
	IMarker,
} from "@sureview/v2-mapping-saas";
import { getAssetIcon, getTrackedAssetIcon } from "@/assets/svgIcons";
import { ensureAssetIconsLoaded, resolveIconDefSync, resolveAvailableIconClasses } from "@/assets/assetMarkerIcons";
import { MarkerState, MarkerType } from "@/scripts/mapping/markerTypes";
import { AssetMapLayerItem } from "@/components/mobile-assets/types";
import moment from "moment";

const FieldOpsStore = namespace("fieldOps");

@Component({})
export default class FieldOpsMixin extends Vue {
	public assetItemMarkers: Map<number, IMarker> = new Map<number, IMarker>();
	public trackedAssetItemMarkers: Map<number, IMarker> = new Map<number, IMarker>();

	@FieldOpsStore.State("assetTypes") public assetTypes: Map<string, any>;
	@FieldOpsStore.State("onlineAssets") public assetAndUsersItems: AssetMapLayerItem[];
	@Getter("getFeature") public getFeature: (featureName: string[]) => boolean;
	@Getter("getAssetTypeIconClasses") public assetTypeIconClasses: string;
	@Getter("getAssetTypeIconClassesDefault") public assetTypeIconClassesDefault: string;
	@Action public getTenantSystemPrefs: (live: boolean) => Promise<void>;

	public get customAssetIconsEnabled(): boolean {
		return this.getFeature(["Assets", "CustomMarkerIcon"]);
	}

	public get customAssetIconsTenantSetEnabled(): boolean {
		return this.getFeature(["Assets", "CustomMarkerIcon", "TenantIconSet"]);
	}

	// Empty until the icon preference has loaded, which resolveCustomIconDef treats as "don't
	// restrict".
	public get availableIconClasses(): Set<string> {
		return resolveAvailableIconClasses(
			this.customAssetIconsTenantSetEnabled,
			this.assetTypeIconClasses,
			this.assetTypeIconClassesDefault
		);
	}

	// Undefined leaves the caller on the default marker.
	private resolveCustomIconDef(assetType: any) {
		if (!this.customAssetIconsEnabled || !assetType || !assetType.customIcon) {
			return undefined;
		}

		const iconDef = resolveIconDefSync(assetType.customIcon);
		if (!iconDef) {
			return undefined;
		}

		// Enforcing membership is what reverts organization-only icons to the default marker when
		// the TenantIconSet sub-feature is switched off.
		const available = this.availableIconClasses;
		if (available.size > 0 && !available.has(assetType.customIcon)) {
			return undefined;
		}

		return iconDef;
	}

	// Public, not private: Vue merges this hook with the host component's own created(), and a
	// private member here breaks the base-class contract for any component that declares one
	// (e.g. EnhancedSingleMap) — a type error only the webpack ts-checker surfaces, not `tsc --noEmit`.
	public created(): void {
		if (this.customAssetIconsEnabled) {
			// The marker builders are synchronous, so the icon pack has to be warm before they run.
			ensureAssetIconsLoaded();

			// Promise.resolve because a host whose store doesn't register the action gets undefined
			// back from the dispatch, and calling .catch on that killed the whole component mount.
			if (this.assetTypeIconClassesDefault == null) {
				Promise.resolve(this.getTenantSystemPrefs(false)).catch(() => {
					/* keep rendering leniently */
				});
			}
		}
	}

    public setMarkerIconForItem(itemId: number, selected: boolean) {
		const marker = this.assetItemMarkers.get(itemId);
		if (marker) {
			const item = this.assetAndUsersItems.find(i => i.assetId === itemId);
			const icon = this.getIconForAssetType(item, selected);
			marker.marker.setIcon(icon);
		}
	}

	public getIconForAssetType(asset: AssetMapLayerItem, selected: boolean) {
		let imageUrl: string = "";
		const assetType = this.assetTypes.get(asset.assetTypeId.toString());
		const iconDef = this.resolveCustomIconDef(assetType);
		if(asset.isTrackedAsset)
		{
			imageUrl = getTrackedAssetIcon(iconDef, asset.activeEventShare);
		}
		else
		{
			const iconColor = asset.iconColor ? asset.iconColor : (assetType ? assetType.iconColor : "#000000");
			imageUrl = getAssetIcon(iconColor, asset.isAsset, selected, asset.activeEventShare, iconDef);
		}

		const icon: google.maps.Icon = { url: imageUrl };
		if (!asset.isTrackedAsset && selected) {
			icon.scaledSize = new google.maps.Size(40, 50)
		}
		return icon;
	}

	public getMarkerType(item: AssetMapLayerItem): MarkerType {
		if(item.isTrackedAsset)
		{
			return this.getTrackedAssetMarkerType(item);
		}
		const assetType = this.assetTypes.get(item.assetTypeId.toString()) ? this.assetTypes.get(item.assetTypeId.toString()) : { iconColor: "#000000" };
		const iconColor = item.iconColor ? item.iconColor : (assetType ? assetType.iconColor : "#000000");
		const iconDef = this.resolveCustomIconDef(assetType);
		const imageUrl = getAssetIcon(iconColor, item.isAsset, false, item.activeEventShare, iconDef);
		const markerType = new MarkerType({ imageUrl } as MarkerState, null);
		return markerType;
	}

	public getTrackedAssetMarkerType(item?: AssetMapLayerItem): MarkerType {
        const assetType = item ? this.assetTypes.get(item.assetTypeId.toString()) : undefined;
        const iconDef = this.resolveCustomIconDef(assetType);
        const imageUrl = getTrackedAssetIcon(iconDef, item ? item.activeEventShare : false);
        const markerType = new MarkerType({ imageUrl } as MarkerState, null);
        return markerType;
    }

	public clearAssetMarkers(): void
	{
		if(this.assetItemMarkers)
		{
			this.assetItemMarkers.forEach(m => {
				try
				{
					m.remove();
				}
				catch
				{
					// ...
				}
			});
		}

		if(this.trackedAssetItemMarkers)
		{
			this.trackedAssetItemMarkers.forEach(m => {
				try
				{
					m.remove();
				}
				catch
				{
					// ...
				}
			});
		}
	}
}
